/**
 * rs-concept.js — CONCEPT MOCKUP homepage behaviour (dev theme only, 2026-10-09).
 * Owned by sections/rs-concept-home.liquid. Everything is scoped to `[data-rsc]`.
 *
 *   - Pager: N full-viewport panels in a native horizontal scroll-snap container (round 12: home,
 *     about, then one page per real collection — this file is generic over the count, reading the
 *     panels, their names, kinds and labels out of the DOM). Native
 *     touch/trackpad swiping does the work; this file adds arrows, dots, keyboard arrows,
 *     mouse click-drag (with a threshold so clicks still work), a URL hash per panel
 *     (written with replaceState on purpose: a swipe must not pile up history entries, so
 *      Back leaves the page rather than stepping back a panel)
 *     (#about / #catalog) and a page-turn shadow that only moves a fixed overlay — the panels
 *     themselves are never transformed, because transforms break scroll-snap.
 *   - The current panel's name is stamped on the root (data-rsc-panel) and its KIND — home / about /
 *     catalog — on data-rsc-kind, which is what rs-concept.css styles by (a collection page's name
 *     is its handle). While panel 1 is current the
 *     dots/hint element is MOVED into the hero (it then scrolls away with the photo instead of
 *     floating over the tiles); on panels 2…N it stays FIXED to the viewport (round 13 — moving it
 *     into the panel's folio buried it 217px below the fold at 1440x900, so the pager's only
 *     position indicator was invisible on the About panel; it sits on the arrows' line there instead).
 *   - The menu: since 2026-10-16 (John: "the hamburger menu should look the same on the homepage as
 *     on the CLPs") the header's one glyph, [data-rsc-flyout], opens the THEME flyout #MobileNav by
 *     dispatching Expanse's mobileNav:open / mobileNav:close at itself — assets/header.drawer.js
 *     does the slide, focus trap, Esc, outside-click and the focus return (to the glyph). The
 *     concept's own drawer and search dialogs are no longer in the section's markup; their code
 *     below (hidden attribute + .is-open, focus trap, Escape, scrim, `inert` on the pager) is
 *     null-safe without them and stays for a one-commit restore.
 *   - Bag badge: the theme's .cart-link__bubble, first read from /cart.js here, kept in step after
 *     that by assets/rs-cart-badge.js like every other header badge.
 *
 * Idempotent: init() marks the root and the Shopify editor's section:load re-runs it.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* THE CUE IS RETIRED ONCE PER SESSION ACROSS THE WHOLE SURFACE — round 14. The browse strips on
     the real collection and product pages (assets/rs-concept-browse.js, read before paint by
     snippets/rs-concept-browse-strip.liquid) have always used this sessionStorage key; the concept
     pager kept its own in-page flag and so neither read nor wrote it. Measured both ways: retire
     the cue on a collection page and the concept page still nagged; step the concept pager and the
     collection strip still nagged. One key, both directions. */
  var HINT_KEY = 'rscx-hint-seen';
  function hintSeen() {
    try { return sessionStorage.getItem(HINT_KEY) === '1'; } catch (e) { return false; }
  }
  function markHintSeen() {
    try { sessionStorage.setItem(HINT_KEY, '1'); } catch (e) { /* private window: the cue stays */ }
    document.documentElement.classList.add('rscx-hint-seen');
  }
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function init(root) {
    if (!root || root.dataset.rscInit === '1') return;
    root.dataset.rscInit = '1';

    var pager = root.querySelector('[data-rsc-pager]');
    if (!pager) return;
    var panels = Array.prototype.slice.call(pager.querySelectorAll('.rsc-panel'));
    // Page mode renders ONE panel (layout_mode "page" — the hero at the top of a normal page). Only
    // the real pager owns carousel behaviour: the live-region page announcements, ArrowLeft and
    // Home/End. The hand-off into the browse chain stays in both modes (below).
    var isPager = panels.length > 1;
    var names = panels.map(function (p) { return p.getAttribute('data-panel') || ''; });
    var labels = panels.map(function (p) { return p.getAttribute('aria-label') || ''; });
    // Round 12: the pager is home, about, then ONE PANEL PER REAL COLLECTION, so a panel's NAME is
    // its collection handle and can no longer be what the CSS styles by. `kinds` (home/about/catalog)
    // is the stable stamp; `aliases` carries the legacy hash of a renamed panel — the first
    // collection panel answers to "catalog" so every #catalog link made before this round still lands.
    var kinds = panels.map(function (p) { return p.getAttribute('data-panel-kind') || p.getAttribute('data-panel') || ''; });
    var aliases = panels.map(function (p) { return p.getAttribute('data-panel-alias') || ''; });
    var prevBtn = root.querySelector('[data-rsc-prev]');
    var nextBtn = root.querySelector('[data-rsc-next]');
    // The hand-off <a> to the first real collection: shown in place of the next arrow on the last panel.
    var goLink = root.querySelector('[data-rsc-go]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-rsc-dot]'));
    var hint = root.querySelector('[data-rsc-hint]');
    var counter = root.querySelector('[data-rsc-counter]');
    var turn = root.querySelector('[data-rsc-turn]');
    var live = root.querySelector('[data-rsc-live]');
    var hero = root.querySelector('[data-rsc-hero]');
    var ui = root.querySelector('[data-rsc-ui]');
    var uiHome = ui ? ui.nextSibling : null; // where the UI goes back to when it leaves the hero
    var current = 0;
    var settleTimer = null;
    var hintDone = false;
    var suppressHash = false;

    /* ---------- helpers ---------- */
    function width() { return pager.clientWidth || window.innerWidth; }
    function indexFromScroll() { return Math.max(0, Math.min(panels.length - 1, Math.round(pager.scrollLeft / width()))); }
    function behavior() { return REDUCED ? 'auto' : 'smooth'; }

    // Panel 1: the dots/hint live inside the hero (absolute — tucked above the card strip on desktop,
    // hidden on phones), so they scroll off with the photo and never land on a tile.
    // Panels 2…N (round 13): back to the FIXED viewport placement. They used to be moved into the
    // panel's folio, which is at the foot of content that is taller than the viewport — at 1440x900
    // that put the pager's only position indicator at y1117, 217px below the fold, on the very panel
    // it was indicating. Fixed is on screen at every width whatever the panel's own scroll is doing,
    // and rs-concept.css puts it on the pager arrows' own line there.
    function placeUi() {
      if (!ui || !hero) return;
      var wantInHero = current === 0;
      var target = wantInHero ? hero : root;
      if (ui.parentNode === target) return;
      var hadFocus = ui.contains(document.activeElement);
      var focused = hadFocus ? document.activeElement : null;
      if (wantInHero) hero.appendChild(ui);
      else root.insertBefore(ui, uiHome);
      ui.classList.toggle('rsc-pager-ui--in-hero', wantInHero);
      root.classList.toggle('rsc--ui-in-hero', wantInHero);
      if (focused && typeof focused.focus === 'function') focused.focus({ preventScroll: true });
    }

    function goTo(i, instant) {
      i = Math.max(0, Math.min(panels.length - 1, i));
      var left = i * width();
      if (instant) {
        pager.classList.add('is-jumping');
        pager.scrollLeft = left;
        // Two frames so the browser applies scroll-behavior:auto before we remove it.
        requestAnimationFrame(function () { requestAnimationFrame(function () { pager.classList.remove('is-jumping'); }); });
        setCurrent(i);
      } else {
        try { pager.scrollTo({ left: left, behavior: behavior() }); } catch (e) { pager.scrollLeft = left; }
      }
    }

    function setCurrent(i) {
      if (i === current && pager.dataset.rscReady === '1') return;
      current = i;
      pager.dataset.rscReady = '1';
      root.setAttribute('data-rsc-panel', names[i] || String(i));
      root.setAttribute('data-rsc-kind', kinds[i] || 'catalog');
      if (counter) counter.textContent = (i + 1) + ' / ' + panels.length;
      if (prevBtn) prevBtn.hidden = i === 0;
      // Last panel: the next ARROW gives way to the hand-off LINK, so the step past the end of the
      // pager is a real navigation to the collection's own page rather than a dead control.
      var atEnd = i === panels.length - 1;
      if (nextBtn) nextBtn.hidden = atEnd;
      if (goLink) goLink.hidden = !atEnd;
      // The dots are plain buttons (not a tab set — they control <section>s, not tabpanels), so the
      // state is aria-current and all three stay in the tab order.
      dots.forEach(function (d, k) {
        var on = k === i;
        d.classList.toggle('is-active', on);
        if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      // Off-screen panels are also `inert`, so their links leave the tab order and a keyboard /
      // screen-reader user is never tabbed into content announced as hidden. The pager itself keeps
      // tabindex=0, so the arrow keys still turn pages. (The dialogs' setInert() targets the pager
      // element, not the panels — the two never fight over the same attribute.)
      panels.forEach(function (p, k) {
        var off = k !== i;
        p.setAttribute('aria-hidden', off ? 'true' : 'false');
        if (off) p.setAttribute('inert', ''); else p.removeAttribute('inert');
      });
      if (live && isPager) live.textContent = 'Page ' + (i + 1) + ' of ' + panels.length + ': ' + labels[i];
      // Round 12: the hint retires when the visitor has actually REACHED page 2, not the moment a
      // swipe starts — on desktop it is now the standing "you can turn the page" cue, and a flick
      // that snaps back should leave it in place. The dots and arrows never retire.
      if (i > 0) dismissHint();
      updateHash(i);
      placeUi();
    }

    function updateHash(i) {
      if (suppressHash || !window.history || !history.replaceState) return;
      var base = location.pathname + location.search;
      var want = i === 0 ? base : base + '#' + names[i];
      var have = location.pathname + location.search + (location.hash || '');
      if (want !== have) { try { history.replaceState(history.state, '', want); } catch (e) { /* ignore */ } }
    }

    function dismissHint() {
      if (hintDone) return;
      hintDone = true;
      markHintSeen();
      if (hint) hint.classList.add('is-done');
    }

    /* ---------- page-turn shadow (fixed strip tracks the boundary between panels) ---------- */
    var turnEnabled = !!turn && !REDUCED && root.dataset.pageTurn === 'true';
    var ticking = false;
    function paintTurn() {
      ticking = false;
      if (!turnEnabled) return;
      var w = width();
      var p = pager.scrollLeft / w;
      var frac = p - Math.floor(p);
      if (frac < 0.005 || frac > 0.995) { turn.style.opacity = '0'; return; }
      var boundary = (1 - frac) * w; // x of the edge where the outgoing page meets the incoming one
      turn.style.transform = 'translateX(' + boundary.toFixed(1) + 'px)';
      turn.style.opacity = (Math.sin(frac * Math.PI) * 0.95).toFixed(3);
    }

    /* ---------- scroll tracking ---------- */
    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(paintTurn); }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, 110);
    }
    function onSettle() { setCurrent(indexFromScroll()); paintTurn(); }
    pager.addEventListener('scroll', onScroll, { passive: true });
    if ('onscrollend' in window) pager.addEventListener('scrollend', onSettle);
    // A vertical scroll inside a panel 2…N also means the visitor is exploring: retire the hint so
    // the fixed "Swipe to explore" line never floats over content they are reading. Panel 1 is
    // excluded (round 12) — scrolling down to the department tiles is not turning a page, and the
    // hint is desktop's standing cue that the pager turns.
    panels.forEach(function (p, k) {
      if (k === 0) return;
      p.addEventListener('scroll', dismissHint, { passive: true, once: true });
    });

    /* ---------- controls ---------- */
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { goTo(parseInt(d.getAttribute('data-rsc-dot'), 10) || 0); });
    });

    function isTypingTarget(el) {
      if (!el) return false;
      var tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    }
    // Past the last panel the pager hands off to the REAL collection page. Swiping, the arrow key and
    // the forward control all end up here, so the gesture a visitor actually tries is the one that
    // works — John, 2026-10-12: "i cant get past the page after hp".
    function handOff() {
      if (!goLink) return false;
      var href = goLink.getAttribute('href');
      if (!href) return false;
      window.location.href = href;
      return true;
    }
    function forward() {
      if (current >= panels.length - 1) { return handOff(); }
      goTo(current + 1);
      return true;
    }

    document.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (root.classList.contains('is-locked')) return; // drawer / search open
      if (isTypingTarget(e.target)) return;
      if (!isPager) {
        // Page mode (2026-10-16): there is nothing to turn, so ArrowLeft, Home and End are left to
        // the browser. ArrowRight still hands off into the browse chain — the same rule as the
        // story and collection pages (assets/rs-concept-browse.js), whose ArrowLeft comes back
        // here — but only with focus on the page itself or on the go arrow, never from a control
        // that has its own arrow-key meaning (the growers slider, a listbox, the theme header).
        var a = document.activeElement;
        var onPage = !a || a === document.body || a === document.documentElement || a === root || a === pager || a === goLink;
        if (e.key === 'ArrowRight' && onPage && handOff()) e.preventDefault();
        return;
      }
      if (e.key === 'ArrowRight') { e.preventDefault(); forward(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
      else if (e.key === 'Home' && e.target === pager) { e.preventDefault(); goTo(0); }
      else if (e.key === 'End' && e.target === pager) { e.preventDefault(); goTo(panels.length - 1); }
    });

    /* ---------- swipe past the end ----------
       Inside the pager, a horizontal touch drag on the last panel has nowhere to scroll, so it dies
       silently. Catch it and hand off. Only fires when the pager is already resting on the last
       panel, the drag is clearly horizontal, and it travelled far enough to be deliberate. */
    (function () {
      var sx = null, sy = null, horiz = false;
      pager.addEventListener('touchstart', function (e) {
        if (!e.touches || e.touches.length !== 1) { sx = null; return; }
        sx = e.touches[0].clientX; sy = e.touches[0].clientY; horiz = false;
      }, { passive: true });
      pager.addEventListener('touchmove', function (e) {
        if (sx === null || !e.touches || !e.touches.length) return;
        var dx = e.touches[0].clientX - sx, dy = e.touches[0].clientY - sy;
        if (!horiz && Math.abs(dy) > Math.abs(dx)) { sx = null; return; } // a vertical scroll, leave it
        if (Math.abs(dx) > 10) horiz = true;
      }, { passive: true });
      pager.addEventListener('touchend', function (e) {
        if (sx === null || !horiz) { sx = null; return; }
        var t = e.changedTouches && e.changedTouches[0];
        var dx = t ? t.clientX - sx : 0;
        sx = null;
        if (dx > -70) return;                                   // not a forward swipe
        if (current < panels.length - 1) return;                 // the pager can still scroll itself
        if (pager.scrollLeft < (panels.length - 1) * width() - 4) return; // still settling
        handOff();
      }, { passive: true });
    })();

    /* ---------- mouse click-drag (desktop). Touch stays native. ---------- */
    var drag = null;
    pager.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('input, textarea, select, [data-rsc-nodrag]')) return;
      drag = { x: e.clientX, y: e.clientY, startLeft: pager.scrollLeft, startIndex: current, moved: false, id: e.pointerId };
    });
    pager.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(e.clientY - drag.y)) return;
        drag.moved = true;
        pager.classList.add('is-dragging');
        try { pager.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
      e.preventDefault();
      pager.scrollLeft = drag.startLeft - dx;
      onScroll();
    });
    function endDrag(e) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var d = drag; drag = null;
      if (!d.moved) return;
      var dx = (e ? e.clientX : d.x) - d.x;
      var target = d.startIndex;
      if (Math.abs(dx) > width() * 0.12) target += dx < 0 ? 1 : -1;
      // Dragged forward off the end: hand off to the real collection instead of snapping back.
      if (target > panels.length - 1) {
        pager.classList.remove('is-dragging');
        if (handOff()) return;
      }
      target = Math.max(0, Math.min(panels.length - 1, target));
      // Keep snapping disabled until the smooth scroll settles, then hand control back to CSS.
      try { pager.scrollTo({ left: target * width(), behavior: behavior() }); } catch (err) { pager.scrollLeft = target * width(); }
      var done = false;
      function finish() {
        if (done) return; done = true;
        pager.classList.remove('is-dragging');
        setCurrent(indexFromScroll());
      }
      pager.addEventListener('scrollend', finish, { once: true });
      setTimeout(finish, REDUCED ? 50 : 700);
      // Swallow the click that follows a drag so links under the cursor do not fire.
      var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      pager.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(function () { pager.removeEventListener('click', swallow, { capture: true }); }, 0);
    }
    pager.addEventListener('pointerup', endDrag);
    pager.addEventListener('pointercancel', endDrag);
    pager.addEventListener('dragstart', function (e) { if (drag && drag.moved) e.preventDefault(); });

    /* ---------- keep the current panel aligned when the viewport changes (rotation, resize) ---------- */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { goTo(current, true); placeUi(); paintTurn(); }, 80);
    });

    /* ---------- deep link / reload keeps place ---------- */
    // A panel answers to its own name and to its legacy alias, so #catalog still finds the first
    // collection page after round 12 renamed it to the collection handle.
    function indexForHash(h) {
      if (!h) return -1;
      var i = names.indexOf(h);
      return i >= 0 ? i : aliases.indexOf(h);
    }
    (function restore() {
      var i = indexForHash((location.hash || '').replace('#', ''));
      if (i > 0) { goTo(i, true); } else { setCurrent(0); }
      // Already seen it somewhere else this session (a collection or product strip, or an earlier
      // visit to this page)? Then it starts retired — no flash, no nag, the line kept either way.
      hintDone = i > 0 || hintDone || hintSeen();
      if (hintDone) { markHintSeen(); if (hint) hint.classList.add('is-done'); }
    })();
    window.addEventListener('hashchange', function () {
      var i = indexForHash((location.hash || '').replace('#', ''));
      if (i >= 0 && i !== current) goTo(i);
    });

    /* ---------- bag count ---------- */
    // 2026-10-16: the badge is the theme's own .cart-link__bubble (markup from header.icons.liquid),
    // so assets/rs-cart-badge.js reconciles it with the header's after every cart write and on
    // pageshow. This first read keeps the Liquid-rendered count and the link's accessible name
    // honest on a page served from cache.
    var bagCount = root.querySelector('[data-rsc-bag-count]');
    var bagLink = root.querySelector('[data-rsc-bag]');
    if (bagCount && window.fetch) {
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var n = cart.item_count || 0;
          bagCount.textContent = String(n);
          var bubble = bagCount.closest('.cart-link__bubble');
          if (bubble) bubble.classList.toggle('cart-link__bubble--visible', n > 0);
          else bagCount.hidden = n === 0;
          if (bagLink) bagLink.setAttribute('aria-label', 'Bag, ' + n + (n === 1 ? ' item' : ' items'));
        })
        .catch(function () { /* badge keeps the Liquid-rendered count */ });
    }

    /* ---------- the theme flyout (2026-10-16) ---------- */
    // John: "the hamburger menu should look the same on the homepage as on the CLPs". The header's
    // glyph [data-rsc-flyout] opens #MobileNav, the theme's own hamburger flyout, which the stock
    // header renders on index underneath this section (rs-concept.css §0 keeps its drawers
    // renderable). Expanse's header-drawer (assets/header.drawer.js) listens on document for
    // mobileNav:open / mobileNav:close and owns the rest — the slide, the focus trap, Esc,
    // outside-click, and the focus RETURN, which goes to the open event's target. That is why the
    // event is dispatched from this button and not relayed through the hidden stock trigger: a
    // relayed click would hand focus to a display:none element when the panel closes. The toggle
    // reads the drawer's real state, as header.toggle-menu.js does since 2026-10-12; aria-expanded
    // follows the drawer's own opened/closed events so a close by Esc or an outside click is
    // reflected too (the cart drawer fires the same events — the sync reads the flyout's own state, 2026-10-27).
    var flyBtn = root.querySelector('[data-rsc-flyout]');
    if (flyBtn) {
      var flyoutEl = function () { return document.querySelector('header-drawer.site-header__mobile-nav'); };
      flyBtn.addEventListener('click', function () {
        var d = flyoutEl();
        if (!d) { location.href = '/search'; return; } // no theme header on the page: the glyph still means "search"
        var isOpen = d.classList.contains('is-active');
        flyBtn.dispatchEvent(new CustomEvent(isOpen ? 'mobileNav:close' : 'mobileNav:open', { bubbles: true }));
        flyBtn.setAttribute('aria-expanded', String(!isOpen));
      });
      // 2026-10-27 (audit PGL-040 / A11Y-04, reported from a keyboard activation): mirror the drawer's REAL state on the
      // next frame rather than trusting the event target (the cart drawer fires the same events) or the value read at
      // click time — prepareTransition toggles is-active around the event, so a same-tick read can lag the drawer.
      // The click-time write above stays as the instant acknowledgement; this is the word that sticks.
      var syncExpanded = function () {
        requestAnimationFrame(function () {
          var d = flyoutEl();
          flyBtn.setAttribute('aria-expanded', String(!!(d && d.classList.contains('is-active'))));
        });
      };
      document.addEventListener('headerDrawer:opened', syncExpanded);
      document.addEventListener('headerDrawer:closed', syncExpanded);
    }

    /* ---------- drawer + search dialogs ---------- */
    var scrim = root.querySelector('[data-rsc-scrim]');
    var dialogs = {
      drawer: root.querySelector('[data-rsc-drawer]'),
      search: root.querySelector('[data-rsc-search]')
    };
    var openDialog = null;
    var opener = null;
    // Everything outside the dialog that could still take focus or a click while it is open.
    var inertables = [pager, prevBtn, nextBtn, ui].filter(Boolean);
    function setInert(on) {
      inertables.forEach(function (el) {
        if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
      });
    }

    function focusables(el) {
      return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (n) {
        return !n.hidden && n.offsetParent !== null;
      });
    }

    function open(name, btn) {
      var el = dialogs[name];
      if (!el || openDialog) return;
      openDialog = el; opener = btn || document.activeElement;
      el.hidden = false;
      if (scrim) scrim.hidden = false;
      root.classList.add('is-locked');
      setInert(true);
      // Force a style flush so the transition runs from the hidden state.
      void el.offsetWidth;
      el.classList.add('is-open');
      if (scrim) scrim.classList.add('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      var first = name === 'search' ? el.querySelector('[data-rsc-search-input]') : el.querySelector('[data-rsc-close]');
      (first || focusables(el)[0] || el).focus({ preventScroll: true });
    }

    function close() {
      if (!openDialog) return;
      var el = openDialog; openDialog = null;
      el.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      root.classList.remove('is-locked');
      setInert(false);
      var btn = opener; opener = null;
      var finish = function () { el.hidden = true; if (scrim) scrim.hidden = true; };
      if (REDUCED) finish(); else setTimeout(finish, 380);
      root.querySelectorAll('[data-rsc-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      // The opener sits inside the pager, which was `inert` until setInert(false) a moment ago; Chrome
      // applies that change at the next style recalc, so focus() in the same tick is refused. Reading
      // a layout property forces the recalc first, and focus returns to the hamburger / search button.
      if (btn && typeof btn.focus === 'function') { void btn.offsetWidth; btn.focus({ preventScroll: true }); }
    }

    root.querySelectorAll('[data-rsc-open]').forEach(function (b) {
      b.addEventListener('click', function () { open(b.getAttribute('data-rsc-open'), b); });
    });
    root.querySelectorAll('[data-rsc-close]').forEach(function (b) { b.addEventListener('click', close); });
    if (scrim) scrim.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (!openDialog) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var list = focusables(openDialog);
      if (!list.length) { e.preventDefault(); openDialog.focus(); return; }
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || !openDialog.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Keep focus inside an open dialog if it escapes by other means (e.g. a click on the scrim area).
    document.addEventListener('focusin', function (e) {
      if (openDialog && !openDialog.contains(e.target)) {
        var list = focusables(openDialog);
        (list[0] || openDialog).focus({ preventScroll: true });
      }
    });

    paintTurn();
  }

  function boot() {
    document.querySelectorAll('[data-rsc]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  // Shopify theme editor re-renders the section; re-bind on the fresh DOM.
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target && e.target.querySelector ? e.target.querySelector('[data-rsc]') : null;
    if (r) init(r);
  });
})();
